import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import type { BillingStructure } from '@/types';
import type { RateMultiplier } from '@/types/RateMultiplier';
import { ThresholdSection } from './ThresholdSection';
import { isStraightTimeMultiplier, getSuggestedDayRateThreshold, getSuggestedWeeklyThreshold, MULTIPLIER_SUGGESTIONS, generateMultiplierCode, type RoleInfo, type MultiplierLiveValues } from './multiplierUtils';
import { ThresholdBadge, MultiplierListItem } from './MultiplierListItem';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { formatCurrency } from '@/utils/formatters';
import { Trash2, X } from 'lucide-react';
import { useScrollFadeMask } from '@/hooks/useScrollFade';

interface MultiplierDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  multiplier?: RateMultiplier | null;
  existingMultipliers: RateMultiplier[];
  billingStructure: BillingStructure | null;
  minimumBillableHours?: number | null;
  roles?: RoleInfo[];
  dayRate?: number | null;
  primaryRoleName?: string | null;
  onSave: (multiplier: RateMultiplier) => void;
  onDelete?: (multiplier: RateMultiplier) => void;
  onLiveThresholdChange?: (threshold: number | null) => void;
  onCommittedThresholdChange?: (threshold: number | null) => void;
  /** Called on every form change so parent can pass live values to the card header */
  onLiveValuesChange?: (values: MultiplierLiveValues) => void;
  /** When true, hides threshold sections and skips threshold validation (for extended tiers) */
  extendedTierMode?: boolean;
  /** Minimum rate for extended tiers — must exceed this value */
  extendedTierMinRate?: number;
}

const DUPLICATE_NAME_ERROR = 'A multiplier with this name already exists.';

export const MultiplierDetailSheet: React.FC<MultiplierDetailSheetProps> = ({
  open,
  onOpenChange,
  mode,
  multiplier,
  existingMultipliers,
  billingStructure,
  minimumBillableHours,
  roles,
  dayRate,
  primaryRoleName,
  onSave,
  onDelete,
  onLiveThresholdChange,
  onCommittedThresholdChange,
  onLiveValuesChange,
  extendedTierMode,
  extendedTierMinRate,
}) => {
  const [name, setName] = useState('');
  const [rate, setRate] = useState<number | null>(null);

  // Threshold config — consolidated into a single state object to prevent partial updates
  interface ThresholdConfig {
    shiftEnabled: boolean;
    shiftThreshold: number | null;
    shiftBillingStructures: string[] | null;
    weeklyEnabled: boolean;
    weeklyThreshold: number | null;
    weeklyBillingStructures: string[] | null;
  }
  const THRESHOLD_DEFAULTS: ThresholdConfig = {
    shiftEnabled: false, shiftThreshold: null, shiftBillingStructures: null,
    weeklyEnabled: false, weeklyThreshold: null, weeklyBillingStructures: null,
  };
  const [thresholds, setThresholds] = useState<ThresholdConfig>(THRESHOLD_DEFAULTS);
  const { shiftEnabled, shiftThreshold, shiftBillingStructures, weeklyEnabled, weeklyThreshold, weeklyBillingStructures } = thresholds;
  const updateThresholds = (patch: Partial<ThresholdConfig>) => setThresholds(prev => ({ ...prev, ...patch }));

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [calcRoleIndex, setCalcRoleIndex] = useState(0);


  const nameInputRef = useRef<HTMLInputElement>(null);

  // Notify parent of live threshold changes for inline label
  useEffect(() => {
    onLiveThresholdChange?.(shiftEnabled ? shiftThreshold : null);
  }, [shiftEnabled, shiftThreshold, onLiveThresholdChange]);

  // Report live values to parent so MultiplierListItem can show them
  useEffect(() => {
    if (onLiveValuesChange && mode === 'edit') {
      onLiveValuesChange({ name, rate, shiftEnabled, shiftThreshold, weeklyEnabled, weeklyThreshold });
    }
  }, [name, rate, shiftEnabled, shiftThreshold, weeklyEnabled, weeklyThreshold, onLiveValuesChange, mode]);

  // Commit the current threshold for sort-position updates (blur passes the value directly)
  const commitThreshold = (value: number | null) => {
    onCommittedThresholdChange?.(shiftEnabled ? value : null);
  };

  // Roles with rate data for calc text cycling
  const ratedRoles = useMemo(
    () => (roles ?? []).filter(r => r.name && r.rate && r.rate > 0),
    [roles]
  );
  const activeCalcRole = ratedRoles.length > 0 ? ratedRoles[calcRoleIndex % ratedRoles.length] : null;
  const activeDayRate = activeCalcRole?.rate ?? dayRate;
  const activeRoleName = activeCalcRole?.name ?? primaryRoleName;

  // Derive whether billing structure is uniform across all roles
  const uniformBillingStructure = useMemo(() => {
    if (!roles || roles.length === 0) return billingStructure;
    const first = roles[0].billingStructure;
    if (!first) return null;
    return roles.every(r => r.billingStructure === first) ? first as BillingStructure : null;
  }, [roles, billingStructure]);

  // Synchronously derive the code of the multiplier being edited — no useEffect delay
  const editingCode = open && mode === 'edit' && multiplier ? multiplier.code : null;

  // Reset form when sheet opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && multiplier) {
        setName(multiplier.name);
        setRate(multiplier.defaultMultiplier);
        const hasShift = multiplier.defaultPerShiftThresholdHours != null;
        const hasWeekly = multiplier.defaultPerWeekThresholdHours != null;
        setThresholds({
          shiftEnabled: hasShift,
          shiftThreshold: hasShift ? multiplier.defaultPerShiftThresholdHours! : null,
          shiftBillingStructures: hasShift ? (multiplier.perShiftBillingStructures ?? null) : null,
          weeklyEnabled: hasWeekly,
          weeklyThreshold: hasWeekly ? multiplier.defaultPerWeekThresholdHours! : null,
          weeklyBillingStructures: hasWeekly ? (multiplier.perWeekBillingStructures ?? null) : null,
        });
      } else {
        setName('');
        setRate(null);
        // Extended tier mode: always start with thresholds disabled
        if (extendedTierMode) {
          setThresholds(THRESHOLD_DEFAULTS);
          setErrors({});
          setCalcRoleIndex(0);
          return;
        }
        // First overtime tier: default Per Shift on, Weekly off.
        // Subsequent tiers: inherit toggle state from the most recent multiplier.
        const nonStraightTime = existingMultipliers.filter(m => !isStraightTimeMultiplier(m.code));
        const lastMultiplier = nonStraightTime.length > 0 ? nonStraightTime[nonStraightTime.length - 1] : null;
        const defaultBillingStructures: string[] = uniformBillingStructure
          ? [uniformBillingStructure]
          : ['day_rate', 'hourly'];
        const freshSuggestion = getSuggestedDayRateThreshold(roles ?? [], minimumBillableHours, existingMultipliers);

        if (!lastMultiplier) {
          setThresholds({
            shiftEnabled: true, shiftThreshold: freshSuggestion, shiftBillingStructures: defaultBillingStructures,
            weeklyEnabled: false, weeklyThreshold: null, weeklyBillingStructures: null,
          });
        } else {
          const lastHadShift = lastMultiplier.defaultPerShiftThresholdHours != null;
          const lastHadWeekly = lastMultiplier.defaultPerWeekThresholdHours != null;
          setThresholds({
            shiftEnabled: lastHadShift,
            shiftThreshold: lastHadShift ? freshSuggestion : null,
            shiftBillingStructures: lastHadShift ? (lastMultiplier.perShiftBillingStructures ?? defaultBillingStructures) : null,
            weeklyEnabled: lastHadWeekly,
            weeklyThreshold: null,
            weeklyBillingStructures: lastHadWeekly ? (lastMultiplier.perWeekBillingStructures ?? null) : null,
          });
        }
      }
      setErrors({});
      setCalcRoleIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, multiplier, existingMultipliers, extendedTierMode]);

  const existingNames = useMemo(
    () =>
      new Set(
        existingMultipliers
          .filter((m) => editingCode ? m.code !== editingCode : true)
          .map((m) => m.name.trim().toLowerCase())
      ),
    [existingMultipliers, editingCode]
  );

  const taggedSuggestions = useMemo(() => {
    const existingNamesAll = new Set(existingMultipliers.map((m) => m.name.trim().toLowerCase()));
    return MULTIPLIER_SUGGESTIONS.map((s) => ({
      ...s,
      isUsed: existingNamesAll.has(s.name.toLowerCase()),
    }));
  }, [existingMultipliers]);

  const handleSuggestionClick = (suggestion: { name: string; rate: number }) => {
    setName(suggestion.name);
    setRate(suggestion.rate);
    if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
    if (errors.rate) setErrors((prev) => ({ ...prev, rate: '' }));
  };

  const defaultBillingStructures: string[] = uniformBillingStructure
    ? [uniformBillingStructure]
    : ['day_rate', 'hourly'];

  const handleShiftToggle = (enabled: boolean) => {
    if (enabled) {
      let threshold = shiftThreshold;
      if (threshold === null) {
        const suggested = getSuggestedDayRateThreshold(roles ?? [], minimumBillableHours, existingMultipliers);
        if (suggested !== null) threshold = suggested;
      }
      updateThresholds({ shiftEnabled: true, shiftThreshold: threshold, shiftBillingStructures: defaultBillingStructures });
      onCommittedThresholdChange?.(threshold);
    } else {
      updateThresholds({ shiftEnabled: false, shiftBillingStructures: null });
      onCommittedThresholdChange?.(null);
    }
    if (errors.shift) setErrors((prev) => ({ ...prev, shift: '' }));
  };

  const handleWeeklyToggle = (enabled: boolean) => {
    if (enabled) {
      let threshold = weeklyThreshold;
      if (threshold === null) {
        const suggested = getSuggestedWeeklyThreshold(roles ?? [], existingMultipliers);
        if (suggested !== null) threshold = suggested;
      }
      updateThresholds({ weeklyEnabled: true, weeklyThreshold: threshold, weeklyBillingStructures: defaultBillingStructures });
    } else {
      updateThresholds({ weeklyEnabled: false, weeklyBillingStructures: null });
    }
    if (errors.weekly) setErrors((prev) => ({ ...prev, weekly: '' }));
  };

  const showAppliesTo = uniformBillingStructure === null;

  // Check if any OTHER multiplier has a weekly threshold
  const otherMultipliersHaveWeekly = useMemo(
    () => existingMultipliers.some((m) => {
      if (editingCode && m.code === editingCode) return false;
      return m.defaultPerWeekThresholdHours != null && m.defaultPerWeekThresholdHours > 0;
    }),
    [existingMultipliers, editingCode]
  );
  const isLastWeeklyThreshold = !otherMultipliersHaveWeekly;

  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    const trimmedName = name.trim();
    if (!trimmedName) {
      newErrors.name = 'Enter a name to continue.';
    } else if (existingNames.has(trimmedName.toLowerCase())) {
      newErrors.name = DUPLICATE_NAME_ERROR;
    }

    if (rate === null) {
      newErrors.rate = 'Set a rate to continue.';
    } else if (extendedTierMode && extendedTierMinRate != null && rate <= extendedTierMinRate) {
      newErrors.rate = `Rate must exceed ${extendedTierMinRate}× (your highest overtime rate).`;
    } else if (rate <= 1.0) {
      newErrors.rate = 'Rate must be greater than 1.0×.';
    }

    if (!extendedTierMode && !shiftEnabled && !weeklyEnabled) {
      newErrors.thresholds = 'Enable at least one threshold to save.';
    }

    if (shiftEnabled) {
      if (shiftThreshold === null || shiftThreshold <= 0) {
        newErrors.shift = 'Enter a per-shift threshold.';
      } else if (shiftThreshold >= 72) {
        newErrors.shift = 'Per-shift threshold must be less than 72 hours.';
      } else {
        // Uniqueness check
        const duplicate = existingMultipliers.find((m) => {
          if (mode === 'edit' && multiplier && m.code === multiplier.code) return false;
          return m.defaultPerShiftThresholdHours === shiftThreshold;
        });
        if (duplicate) {
          newErrors.shift = `Per-shift threshold already used by "${duplicate.name}".`;
        }
      }
      if (showAppliesTo && (!shiftBillingStructures || shiftBillingStructures.length === 0)) {
        newErrors.shift = newErrors.shift || 'Select at least one billing structure for per-shift.';
      }
    }

    if (weeklyEnabled) {
      if (weeklyThreshold === null || weeklyThreshold <= 0) {
        newErrors.weekly = 'Enter a weekly threshold.';
      } else if (weeklyThreshold > 168) {
        newErrors.weekly = 'Weekly threshold must be 168 hours or less.';
      } else {
        const duplicate = existingMultipliers.find((m) => {
          if (mode === 'edit' && multiplier && m.code === multiplier.code) return false;
          return m.defaultPerWeekThresholdHours === weeklyThreshold;
        });
        if (duplicate) {
          newErrors.weekly = `Weekly threshold already used by "${duplicate.name}".`;
        }
      }
      if (showAppliesTo && (!weeklyBillingStructures || weeklyBillingStructures.length === 0)) {
        newErrors.weekly = newErrors.weekly || 'Select at least one billing structure.';
      }
    }

    return newErrors;
  };

  const handleSave = () => {
    if (!isFormValid) {
      setErrors(validate());
      return;
    }

    const code = mode === 'create' ? generateMultiplierCode(name, existingMultipliers) : (multiplier?.code ?? generateMultiplierCode(name, existingMultipliers));

    const result: RateMultiplier = {
      id: mode === 'edit' ? multiplier?.id : undefined,
      code,
      name: name.trim(),
      defaultMultiplier: rate ?? 0,
      defaultPerShiftThresholdHours: shiftEnabled && shiftThreshold != null ? Number(shiftThreshold) : null,
      defaultPerWeekThresholdHours: weeklyEnabled && weeklyThreshold != null ? Number(weeklyThreshold) : null,
      perShiftBillingStructures: shiftEnabled ? shiftBillingStructures : null,
      perWeekBillingStructures: weeklyEnabled ? weeklyBillingStructures : null,
    };

    onSave(result);
    if (mode === 'edit') {
      toast.success('Multiplier saved');
    }
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (multiplier && onDelete) {
      onDelete(multiplier);
      onOpenChange(false);
    }
  };

  // Single source of truth: derive validity, hint, and first error key from validate()
  const { isFormValid, validationHint, firstErrorKey } = useMemo(() => {
    const errs = validate();
    const keys = Object.keys(errs);
    if (keys.length === 0) return { isFormValid: true, validationHint: null, firstErrorKey: null };
    // Prefer name/rate errors first; fall back to shift/weekly if those are the only issues
    const footerKeys = keys.filter(k => k !== 'shift' && k !== 'weekly');
    const hint = footerKeys.length > 0 ? errs[footerKeys[0]] : errs[keys[0]];
    return { isFormValid: false, validationHint: hint, firstErrorKey: keys[0] };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validate is a render-scoped function; its actual deps are listed explicitly
  }, [name, rate, thresholds, existingMultipliers, existingNames, editingCode, mode, multiplier, showAppliesTo, extendedTierMode, extendedTierMinRate]);

  const [hintShake, setHintShake] = useState(false);

  const handleDisabledClick = () => {
    if (isFormValid) return;
    setHintShake(true);
    setTimeout(() => setHintShake(false), 600);

    // Focus the first invalid field
    const focusTargets: Record<string, () => void> = {
      name: () => nameInputRef.current?.focus(),
      rate: () => document.getElementById('multiplier-rate')?.focus(),
      shift: () => document.getElementById('threshold-shift')?.focus(),
      weekly: () => document.getElementById('threshold-weekly')?.focus(),
    };
    if (firstErrorKey) focusTargets[firstErrorKey]?.();
  };

  const isMobile = useIsMobile();
  const scrollFadeRef = useScrollFadeMask();

  const titleText = extendedTierMode
    ? (mode === 'create' ? 'Add Extended Tier' : 'Edit Extended Tier')
    : (mode === 'create' ? 'Add Overtime Tier' : 'Edit Multiplier');
  const descriptionText = extendedTierMode
    ? (mode === 'create' ? 'Configure an extended overtime tier with name and rate' : 'Edit extended tier details')
    : (mode === 'create' ? 'Configure a new overtime multiplier with rate and thresholds' : 'Edit multiplier details');

  const handleAutoFocus = (e: Event) => {
    e.preventDefault();
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  // Preset chips — shared between mobile (under name field) and desktop (beside rate field)
  const availableSuggestions = taggedSuggestions.filter(s => !s.isUsed);
  const presetChips = mode === 'create' && availableSuggestions.length > 0 ? (
    <div className="flex flex-row flex-wrap md:flex-nowrap gap-1.5">
      {availableSuggestions.map((s) => (
        <button
          key={s.name}
          type="button"
          onClick={() => handleSuggestionClick(s)}
          className="inline-flex items-center whitespace-nowrap text-[10px] sm:text-xs lg:text-[11.5px] px-1.5 sm:px-2.5 lg:px-2 py-0.5 sm:py-1 lg:py-0.5 rounded border border-dashed transition-colors border-primary/30 bg-primary/5 text-primary/70 hover:text-primary hover:border-primary/60 hover:bg-primary/10 cursor-pointer"
        >
          {s.name} ({s.rate}x)
        </button>
      ))}
    </div>
  ) : null;

  // Live duplicate name check (shown before save attempt)
  const trimmedName = name.trim();
  const liveNameDuplicate = trimmedName && existingNames.has(trimmedName.toLowerCase());

  // Shared field elements — rendered in both mobile and desktop layouts
  const nameField = (
    <div className="space-y-1.5 relative">
      <Label htmlFor="multiplier-name">Name</Label>
      <Input
        ref={nameInputRef}
        id="multiplier-name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
        }}
        placeholder="e.g. Overtime"
        autoComplete="off"
        className={cn((errors.name || liveNameDuplicate) && 'border-destructive')}
      />
      {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      {!errors.name && liveNameDuplicate && (
        <p className="text-xs text-destructive">{DUPLICATE_NAME_ERROR}</p>
      )}
    </div>
  );

  const rateField = (
    <div className="space-y-1.5">
      <Label htmlFor="multiplier-rate">Rate</Label>
      <div className="relative w-[72px]">
        <Input
          id="multiplier-rate"
          type="number"
          value={rate ?? ''}
          onChange={(e) => {
            const val = e.target.value ? parseFloat(e.target.value) : null;
            setRate(val);
            if (errors.rate) setErrors((prev) => ({ ...prev, rate: '' }));
          }}
          min={1.1}
          max={10}
          step={0.1}
          placeholder="1.5"
          className={cn('w-full pr-5 text-right font-mono', errors.rate && 'border-destructive')}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">x</span>
      </div>
      {errors.rate && <p className="text-xs text-destructive text-right mt-1">{errors.rate}</p>}
    </div>
  );

  const cycleCalcRole = ratedRoles.length > 1 ? () => setCalcRoleIndex(i => (i + 1) % ratedRoles.length) : undefined;

  // Hourly roles: rate is already per hour. Day rate roles: divide by minimum billable hours.
  const activeBaseHourly = activeCalcRole?.billingStructure === 'hourly'
    ? activeDayRate
    : (activeDayRate && minimumBillableHours ? activeDayRate / minimumBillableHours : null);

  const fmtRate = (v: number) => formatCurrency(v, { showCents: v % 1 !== 0 });

  const calcText = rate && rate > 0 && activeBaseHourly && activeBaseHourly > 0 ? (
    <p className="text-[10px] sm:text-[12px] font-mono mt-1">
      <span className="text-primary/60 dark:text-primary">
        <span key={`${calcRoleIndex}-${rate}`} className="inline-block motion-safe:animate-[fadeSlideIn_0.25s_ease-out]">{fmtRate(activeBaseHourly * rate)}</span>/hr
      </span>
      {activeRoleName && (
        cycleCalcRole ? (
          <button
            type="button"
            onClick={cycleCalcRole}
            className="text-muted-foreground hover:text-primary/80 hover:drop-shadow-[0_0_6px_rgba(232,134,12,0.4)] transition-all cursor-pointer ml-1"
          >
            based on <span key={calcRoleIndex} className="inline-block motion-safe:animate-[fadeSlideIn_0.25s_ease-out] dark:text-foreground/70">{activeRoleName}</span> rate ↻
          </button>
        ) : (
          <span className="text-muted-foreground"> based on <span className="dark:text-foreground/70">{activeRoleName}</span> rate</span>
        )
      )}
    </p>
  ) : null;

  const shiftSection = (
    <ThresholdSection
      type="shift"
      mode={mode}
      enabled={shiftEnabled}
      onToggle={handleShiftToggle}
      threshold={shiftThreshold}
      onThresholdChange={(v) => {
        updateThresholds({ shiftThreshold: v });
        if (errors.shift) setErrors((prev) => ({ ...prev, shift: '' }));
      }}
      billingStructures={shiftBillingStructures}
      onBillingStructuresChange={(v) => updateThresholds({ shiftBillingStructures: v })}
      showAppliesTo={showAppliesTo}
      error={errors.shift}
      onCommit={commitThreshold}
    />
  );

  const weeklySection = (
    <ThresholdSection
      type="weekly"
      mode={mode}
      enabled={weeklyEnabled}
      onToggle={handleWeeklyToggle}
      threshold={weeklyThreshold}
      onThresholdChange={(v) => {
        updateThresholds({ weeklyThreshold: v });
        if (errors.weekly) setErrors((prev) => ({ ...prev, weekly: '' }));
      }}
      billingStructures={weeklyBillingStructures}
      onBillingStructuresChange={(v) => updateThresholds({ weeklyBillingStructures: v })}
      showAppliesTo={showAppliesTo}
      error={errors.weekly}
      isLastWeeklyThreshold={isLastWeeklyThreshold}
    />
  );

  const formBody = (
    <div className="space-y-2.5 sm:space-y-4">
      <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
        {nameField}
        {rateField}
      </div>
      {isMobile && calcText && <div className="-mt-2">{calcText}</div>}
      {/* Mobile: always-visible preset chips below rate */}
      {isMobile && presetChips && <div className="-mt-1">{presetChips}</div>}
      {!extendedTierMode && (
        <>
          <div className="border-t border-border/50" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {shiftSection}
            {weeklySection}
          </div>
        </>
      )}
    </div>
  );

  const canDelete = mode === 'edit' && onDelete && multiplier && !isStraightTimeMultiplier(multiplier.code);

  const validationHintEl = validationHint && (
    <p className={cn(
      'text-xs text-center transition-colors',
      hintShake ? 'text-destructive motion-safe:animate-[shake_0.4s_ease-in-out]' : 'text-muted-foreground'
    )}>{validationHint}</p>
  );

  // Mobile: bottom sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          onClose={() => onOpenChange(false)}
          className="max-h-[85vh] flex flex-col rounded-t-xl pb-[max(1.25rem,env(safe-area-inset-bottom))]"
          onOpenAutoFocus={handleAutoFocus}
        >
          <div
            ref={scrollFadeRef}
            className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-2 scroll-fade-y"
          >
            <SheetHeader className="text-left pb-4">
              {!name ? (
                <>
                  <SheetTitle className="heading-label text-lg">
                    {titleText}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {descriptionText}
                  </SheetDescription>
                </>
              ) : (
                <>
                  <SheetTitle asChild>
                    <div className="flex items-center justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="font-mono text-sm font-bold tabular-nums text-primary-text shrink-0">
                          {rate ? `${rate}x` : '—'}
                        </span>
                        <span className="font-heading text-lg uppercase tracking-wide truncate text-foreground">
                          {name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {shiftEnabled && shiftThreshold != null && (
                          <ThresholdBadge hours={shiftThreshold} type="shift" />
                        )}
                        {weeklyEnabled && weeklyThreshold != null && (
                          <ThresholdBadge hours={weeklyThreshold} type="week" />
                        )}
                      </div>
                    </div>
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {descriptionText}
                  </SheetDescription>
                </>
              )}
            </SheetHeader>
            {formBody}
          </div>
          <SheetFooter className="mt-0 flex-col gap-2 px-5 pt-3 border-t border-border/50 bg-background flex-shrink-0">
            {validationHintEl}
            <div className="flex flex-row gap-2 w-full items-center">
              {canDelete ? (
                <button
                  type="button"
                  className="flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-destructive active:translate-y-px transition-colors"
                  onClick={handleDelete}
                  aria-label="Delete multiplier"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              ) : mode === 'create' ? (
                <button
                  type="button"
                  className="flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-foreground active:translate-y-px transition-colors"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              ) : null}
              <Button
                className={cn('flex-1 bg-primary text-primary-foreground font-semibold active:translate-y-px', !isFormValid && 'opacity-50')}
                onClick={isFormValid ? handleSave : handleDisabledClick}
                type="button"
              >
                {mode === 'create' ? 'Add Multiplier' : 'Save'}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: inline accordion (rendered inside the list)
  if (!open) return null;

  // Live header for create mode — reuse MultiplierListItem with live overrides
  const liveHeader = mode === 'create' ? (
    <AnimatedCollapse open={!!name || rate !== null}>
      <div className="border-b border-border/40">
        <MultiplierListItem
          multiplier={{ code: '_NEW', name: 'New Multiplier', defaultMultiplier: null, defaultPerShiftThresholdHours: null, defaultPerWeekThresholdHours: null, perShiftBillingStructures: null, perWeekBillingStructures: null }}
          isStraightTime={false}
          isExpanded
          liveOverrides={{ name: name || 'New Multiplier', rate, shiftEnabled, shiftThreshold, weeklyEnabled, weeklyThreshold }}
        />
      </div>
    </AnimatedCollapse>
  ) : null;

  // Desktop: inline accordion layout — Name+Rate top row, presets beside rate, thresholds side-by-side
  return (
    <div>
      {liveHeader}
      <div className="border-t border-border/40 p-4 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          {nameField}
          {rateField}
        </div>
        {presetChips && (
          <div className="flex items-center pt-6">
            {presetChips}
          </div>
        )}
      </div>
      {!extendedTierMode && (
        <div className="grid grid-cols-2 gap-3">
          {shiftSection}
          {weeklySection}
        </div>
      )}
      {validationHintEl}
      <div className="relative flex items-center justify-between pt-1">
        {canDelete ? (
          <Button variant="destructive" size="sm" onClick={handleDelete} type="button">
            Delete
          </Button>
        ) : <div />}
        {calcText && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-[120px] [&_p]:mt-0 [&_p]:text-sm [&_p]:truncate [&_button]:pointer-events-auto [&_button]:truncate">
            {calcText}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} type="button">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={isFormValid ? handleSave : handleDisabledClick}
            type="button"
            className={cn(!isFormValid && 'opacity-50')}
          >
            Save
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
};
